"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getSpeciesProfile = getSpeciesProfile;
exports.estimateWaterTemp = estimateWaterTemp;
exports.calculateLightSuitability = calculateLightSuitability;
exports.calculatePressureSuitability = calculatePressureSuitability;
exports.windSuitability = windSuitability;
exports.precipCloudSuitability = precipCloudSuitability;
exports.calculateMoonSuitability = calculateMoonSuitability;
exports.calculateSeasonSuitability = calculateSeasonSuitability;
exports.stabilitySuitability = stabilitySuitability;
exports.computePressureTrend6h = computePressureTrend6h;
exports.computePressureTrend24h = computePressureTrend24h;
exports.detectFrontPenalty = detectFrontPenalty;
// Fishing calculation utilities - alla beräkningsfunktioner för fishing score
const constants_1 = require("../config/constants");
const weatherUtils_1 = require("./weatherUtils");
/**
 * Hämtar species profile baserat på key
 */
function getSpeciesProfile(species) {
    const key = species || 'general';
    return constants_1.SPECIES_PROFILES[key] || constants_1.SPECIES_PROFILES.general;
}
/**
 * Estimerar vattentemperatur baserat på lufttemperatur, historik och årstid
 * Förbättrad modell med säsongskompensation och mer realistiska lag-effekter
 */
function estimateWaterTemp(hourWeather, dailyHistory, waterProfile) {
    const todayStr = (new Date(hourWeather.date || hourWeather.time || Date.now()))
        .toISOString()
        .split('T')[0];
    // Get current date for seasonal baseline
    const currentDate = new Date(todayStr);
    const month = currentDate.getMonth(); // 0=Jan, 11=Dec
    // Seasonal baseline water temp (Sweden/Nordic region)
    // Baserat på typiska vattentemperaturer per månad i Sverige
    const seasonalBaseline = [
        2, // Jan - kallt vatten under is
        1, // Feb - kallast
        2, // Mar - börjar värmas
        4, // Apr - fortsätter upp
        8, // Maj - märkbar uppvärmning
        14, // Jun - varmt
        18, // Jul - varmast
        17, // Aug - fortfarande varmt
        14, // Sep - börjar kylas
        10, // Okt - kallare
        6, // Nov - kallt
        3 // Dec - mycket kallt
    ];
    const baselineTemp = seasonalBaseline[month];
    // Get past 7-14 days air temps for better trend
    const past = (dailyHistory || [])
        .filter(d => d.date < todayStr)
        .slice(-14); // Use 2 weeks instead of 3 days
    const pastMeans = past
        .map(d => ((d.tempMax ?? 0) + (d.tempMin ?? 0)) / 2)
        .filter(n => !isNaN(n));
    const airNow = hourWeather.temperature ??
        (hourWeather.tempMax && hourWeather.tempMin ?
            (hourWeather.tempMax + hourWeather.tempMin) / 2 : 12);
    // Average air temp over past period
    const avgPastAirTemp = pastMeans.length > 0
        ? pastMeans.reduce((a, b) => a + b, 0) / pastMeans.length
        : airNow;
    // Depth-dependent lag coefficient (hur snabbt vatten reagerar på lufttemp)
    // Shallow = snabbare, Deep = mycket långsammare
    const lagCoefficients = {
        shallow: { airInfluence: 0.35, seasonalWeight: 0.35, trendWeight: 0.30 },
        medium: { airInfluence: 0.20, seasonalWeight: 0.50, trendWeight: 0.30 },
        deep: { airInfluence: 0.10, seasonalWeight: 0.70, trendWeight: 0.20 }
    };
    const coeff = lagCoefficients[waterProfile.depth] || lagCoefficients.medium;
    // Calculate water temp as weighted average:
    // 1. Seasonal baseline (strongest for deep water)
    // 2. Past air temp trend (medium influence)
    // 3. Current air temp (weakest, especially for deep water)
    const waterTemp = coeff.seasonalWeight * baselineTemp +
        coeff.trendWeight * avgPastAirTemp +
        coeff.airInfluence * airNow;
    // Clamp to realistic range (-2°C to 30°C)
    return Math.max(-2, Math.min(30, waterTemp));
}
/**
 * Beräknar ljusförhållande-lämplighet för fiske
 */
function calculateLightSuitability(hour, sunrise, sunset, cloudiness = 0.5, species) {
    const profile = getSpeciesProfile(species);
    return lightSuitability(hour, sunrise, sunset, cloudiness, profile);
}
/**
 * Beräknar ljusförhållande-lämplighet för fiske (intern helper)
 */
function lightSuitability(hour, sunrise, sunset, cloudiness, profile) {
    const sr = sunrise ? new Date(sunrise).getHours() : 6;
    const ss = sunset ? new Date(sunset).getHours() : 18;
    // Dawn and dusk are peak times
    const sDawn = (0, weatherUtils_1.gaussian)(hour, sr + 1, 1.2) * profile.dawnBoost;
    const sDusk = (0, weatherUtils_1.gaussian)(hour, ss - 1, 1.2) * profile.duskBoost;
    const isNight = hour < sr - 1 || hour > ss + 1;
    const isMid = hour >= sr + 3 && hour <= ss - 3;
    let base = Math.max(sDawn, sDusk);
    // Night is generally poor for most species (except zander)
    if (isNight) {
        base = Math.max(base, profile.nightBoost * 0.3);
    }
    // Midday penalty with cloud cover helping
    if (isMid) {
        const shade = 0.4 + 0.6 * cloudiness;
        base = Math.max(base, profile.middayPenalty * shade);
    }
    return (0, weatherUtils_1.clamp01)(base);
}
/**
 * Beräknar lufttrycks-lämplighet för fiske
 */
function calculatePressureSuitability(p_hPa, trend_hPa6h, trend_hPa24h = 0, species) {
    const profile = getSpeciesProfile(species);
    return pressureSuitability(p_hPa, trend_hPa6h, trend_hPa24h, profile);
}
/**
 * Beräknar lufttrycks-lämplighet för fiske (intern helper)
 */
function pressureSuitability(p_hPa, trend_hPa6h, trend_hPa24h, profile) {
    let sVal;
    // Base pressure value
    if (p_hPa >= 1016 && p_hPa <= 1022)
        sVal = 1.0;
    else if (p_hPa >= 1010 && p_hPa <= 1025)
        sVal = 0.8;
    else if (p_hPa < 1005 || p_hPa > 1030)
        sVal = 0.2;
    else
        sVal = 0.55;
    // Short-term pressure change (6 hours) - immediate impact
    const absCh6h = Math.abs(trend_hPa6h || 0);
    if (absCh6h > 6)
        sVal *= 0.2;
    else if (absCh6h > 4)
        sVal *= 0.3;
    else if (absCh6h > 3)
        sVal *= 0.5;
    else if (absCh6h > 2)
        sVal *= 0.7;
    // Long-term pressure change (24 hours) - sustained weather pattern
    const absCh24h = Math.abs(trend_hPa24h || 0);
    if (absCh24h > 15)
        sVal *= 0.3;
    else if (absCh24h > 10)
        sVal *= 0.5;
    else if (absCh24h > 6)
        sVal *= 0.7;
    // Falling pressure (storm approaching) is worse than rising
    if (trend_hPa6h < -3)
        sVal *= 0.8;
    if (trend_hPa24h < -10)
        sVal *= 0.7;
    return (0, weatherUtils_1.clamp01)(Math.pow(sVal, profile.pressureWeight));
}
/**
 * Beräknar vind-lämplighet för fiske
 */
function windSuitability(speed_ms, gust_ms) {
    const sp = speed_ms ?? 0;
    const gust = gust_ms ?? sp;
    const gustDiff = gust - sp;
    // Base score from average wind speed
    let sSpeed = 1.0;
    if (sp < 0.5)
        sSpeed = 0.4;
    else if (sp >= 1 && sp <= 4)
        sSpeed = 1.0;
    else if (sp >= 4 && sp <= 7)
        sSpeed = 0.9;
    else if (sp >= 7 && sp <= 10)
        sSpeed = 0.7;
    else if (sp >= 10 && sp <= 12)
        sSpeed = 0.4;
    else if (sp >= 12 && sp <= 15)
        sSpeed = 0.2;
    else
        sSpeed = 0.1;
    // Gust score - absolute gust speed matters a lot!
    let sGust = 1.0;
    if (gust < 5)
        sGust = 1.0;
    else if (gust <= 8)
        sGust = 0.9;
    else if (gust <= 12)
        sGust = 0.7;
    else if (gust <= 15)
        sGust = 0.5;
    else if (gust <= 18)
        sGust = 0.3;
    else if (gust <= 22)
        sGust = 0.15;
    else
        sGust = 0.05;
    // Gustiness penalty
    let sGustiness = 1.0;
    if (gustDiff > 10)
        sGustiness = 0.4;
    else if (gustDiff > 8)
        sGustiness = 0.5;
    else if (gustDiff > 6)
        sGustiness = 0.65;
    else if (gustDiff > 4)
        sGustiness = 0.75;
    else if (gustDiff > 2)
        sGustiness = 0.9;
    const combined = Math.min(sSpeed, sGust, sGustiness);
    return (0, weatherUtils_1.clamp01)(combined);
}
/**
 * Beräknar nederbörd/moln-lämplighet för fiske
 */
function precipCloudSuitability(precip_mm, visibility_km, weatherCode) {
    let s = 0.8;
    if (precip_mm === 0)
        s = 0.9;
    else if (precip_mm > 0 && precip_mm <= 0.5)
        s = 1.0;
    else if (precip_mm <= 1)
        s = 0.95;
    else if (precip_mm <= 3)
        s = 0.85;
    else if (precip_mm <= 6)
        s = 0.6;
    else
        s = 0.3;
    if (visibility_km < 1)
        s *= 0.4;
    else if (visibility_km < 3)
        s *= 0.7;
    else if (visibility_km < 5)
        s *= 0.85;
    return (0, weatherUtils_1.clamp01)(s);
}
/**
 * Beräknar månfas-lämplighet för fiske
 */
function calculateMoonSuitability(phase01, hour, sunrise, sunset, species) {
    const profile = getSpeciesProfile(species);
    return moonSuitability(phase01, hour, sunrise, sunset, profile);
}
/**
 * Beräknar månfas-lämplighet för fiske (intern helper)
 */
function moonSuitability(phase01, hour, sunrise, sunset, profile) {
    const sr = sunrise ? new Date(sunrise).getHours() : 6;
    const ss = sunset ? new Date(sunset).getHours() : 18;
    const isNight = hour < sr - 1 || hour > ss + 1;
    let s = 0.7;
    if (isNight && phase01 >= 0.45 && phase01 <= 0.55)
        s = 1.0;
    else if (!isNight && (phase01 <= 0.08 || phase01 >= 0.92))
        s = 0.85;
    return (0, weatherUtils_1.clamp01)(Math.pow(s, profile.moonWeight));
}
/**
 * Beräknar säsongs-lämplighet för fiske
 */
function calculateSeasonSuitability(dateStr, tempC, species) {
    const profile = getSpeciesProfile(species);
    return seasonSuitability(dateStr, tempC, profile);
}
/**
 * Beräknar säsongs-lämplighet för fiske (intern helper)
 */
function seasonSuitability(dateStr, tempC, profile) {
    const sTemp = (0, weatherUtils_1.gaussian)(tempC, profile.tempOpt, profile.tempSigma);
    const m = new Date(dateStr).getMonth() + 1;
    let seasonAdj = 1.0;
    if (m >= 9 && m <= 11)
        seasonAdj = 1.1;
    if (m >= 4 && m <= 5)
        seasonAdj = 1.05;
    return (0, weatherUtils_1.clamp01)(sTemp * seasonAdj);
}
/**
 * Beräknar stabilitets-lämplighet för fiske
 */
function stabilitySuitability(stabilityPct) {
    const x = (0, weatherUtils_1.clamp01)((stabilityPct ?? 50) / 100);
    return (0, weatherUtils_1.clamp01)(0.5 + 0.55 * x);
}
/**
 * Beräknar lufttryckstrend över 6 timmar
 */
function computePressureTrend6h(hourIndex, hourlyHistory) {
    if (!hourlyHistory || hourlyHistory.length < 6 || hourIndex < 6)
        return 0;
    const slice = hourlyHistory.slice(hourIndex - 6, hourIndex);
    const ps = slice.map(h => h.pressure).filter(v => v != null);
    if (ps.length < 6)
        return 0;
    const early = ps.slice(0, 3).reduce((a, b) => a + b, 0) / 3;
    const late = ps.slice(3).reduce((a, b) => a + b, 0) / 3;
    return late - early;
}
/**
 * Beräknar lufttryckstrend över 24 timmar
 */
function computePressureTrend24h(hourIndex, hourlyHistory) {
    if (!hourlyHistory || hourlyHistory.length < 24 || hourIndex < 24)
        return 0;
    const slice = hourlyHistory.slice(hourIndex - 24, hourIndex);
    const ps = slice.map(h => h.pressure).filter(v => v != null);
    if (ps.length < 20)
        return 0;
    const early = ps.slice(0, 6).reduce((a, b) => a + b, 0) / 6;
    const late = ps.slice(-6).reduce((a, b) => a + b, 0) / 6;
    return late - early;
}
/**
 * Detekterar väderfront och returnerar penalty-multiplikator
 */
function detectFrontPenalty(hourIndex, hourlyHistory) {
    if (!hourlyHistory || hourlyHistory.length < 6 || hourIndex < 6)
        return 1.0;
    const pCh = computePressureTrend6h(hourIndex, hourlyHistory);
    const dirs = hourlyHistory
        .slice(hourIndex - 6, hourIndex)
        .map(h => h.windDirection)
        .filter(v => v != null);
    let maxShift = 0;
    for (let i = 1; i < dirs.length; i++) {
        let d = Math.abs(dirs[i] - dirs[i - 1]);
        if (d > 180)
            d = 360 - d;
        if (d > maxShift)
            maxShift = d;
    }
    const strongP = Math.abs(pCh) > 6;
    const strongShift = maxShift > 60;
    if (strongP && strongShift)
        return 0.6;
    if (Math.abs(pCh) > 3 && maxShift > 40)
        return 0.8;
    return 1.0;
}
